import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Info, Shield, FlaskConical } from 'lucide-react';

export const AIAnalysisExplainerDialog = () => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-slate-300 hover:text-white block text-left">
          How AI Analysis Works
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-600" />
            How Evidentica's AI Evaluates Research Quality
          </DialogTitle>
          <DialogDescription>
            Three scoring systems for three article types — all AI-assisted, transparent, and reproducible
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Classification */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">Article Classification</h3>
            <p className="text-sm text-blue-800 mb-3">
              Before scoring, every article is classified into one of three categories. Only one scoring system applies per article:
            </p>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div className="bg-green-50 border border-green-200 rounded p-2 text-center">
                <p className="font-bold text-green-800">Primary Research</p>
                <p className="text-green-700">Methodology Score (0-70)</p>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded p-2 text-center">
                <p className="font-bold text-teal-800">Evidence Synthesis</p>
                <p className="text-teal-700">ESS (0-35)</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded p-2 text-center">
                <p className="font-bold text-purple-800">Case Reports</p>
                <p className="text-purple-700">Clinical Value (0-35)</p>
              </div>
            </div>
          </div>

          {/* Analysis Process */}
          <div>
            <h3 className="text-lg font-semibold text-slate-900 mb-3">Analysis Process</h3>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <h4 className="font-semibold text-slate-800">Document Parsing & Classification</h4>
                  <p className="text-sm text-slate-600">
                    AI extracts text, identifies study type, and routes to the appropriate scoring system.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <h4 className="font-semibold text-slate-800">Type-Specific Evaluation</h4>
                  <p className="text-sm text-slate-600">
                    <strong>Primary Research:</strong> Study design, sample size, follow-up, rigor + data integrity check.<br/>
                    <strong>Evidence Synthesis:</strong> Review design, transparency, synthesis structure, clinical relevance.<br/>
                    <strong>Case Reports:</strong> Documentation quality, novelty, educational value, follow-up.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <h4 className="font-semibold text-slate-800">Deterministic Score Calculation</h4>
                  <p className="text-sm text-slate-600">
                    AI-extracted features are combined using transparent, reproducible formulas. 
                    Visibility Score (0-30) is added for the final Impact Index.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ESS Detail */}
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-teal-900 mb-2">Evidence Synthesis Score (ESS) — max 35</h3>
            <p className="text-sm text-teal-800 mb-3">
              For systematic reviews, meta-analyses, scoping reviews, narrative reviews, and critical appraisals.
            </p>
            <div className="space-y-2 text-sm text-teal-800">
              <div><strong>Review Design (0-15):</strong> Deterministic — based on review type (meta-analysis=15, systematic=12, scoping=10, critical appraisal=8, narrative=6)</div>
              <div><strong>Transparency (0-10):</strong> AI-evaluated — adaptive criteria per review type (PRISMA, search strategy, eligibility, etc.)</div>
              <div><strong>Synthesis Structure (0-5):</strong> Structured=5, Partially=3, Descriptive=1</div>
              <div><strong>Clinical Relevance (0-5):</strong> Guideline-informing=5, High=4, Moderate=2, Low=1</div>
            </div>
          </div>

          {/* Data Integrity */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-green-900 mb-2 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Data Integrity Check (Primary Research only)
            </h3>
            <p className="text-sm text-green-800">
              After calculating the raw methodology score, an AI integrity review is performed.
              Score (0-100) acts as multiplier: <strong>Final = Raw x max(0.5, Integrity/100)</strong>
            </p>
          </div>

          {/* Impact Index */}
          <div className="space-y-2 text-sm text-slate-700">
            <div className="flex justify-between p-2 bg-green-50 rounded">
              <span><strong>Primary Research</strong></span>
              <span>Methodology (0-70) + Visibility (0-30) = max 100</span>
            </div>
            <div className="flex justify-between p-2 bg-teal-50 rounded">
              <span><strong>Evidence Synthesis</strong></span>
              <span>ESS (0-35) + Visibility (0-30) = max 65</span>
            </div>
            <div className="flex justify-between p-2 bg-purple-50 rounded">
              <span><strong>Case Reports</strong></span>
              <span>Clinical Value (0-35) + Visibility (0-30) = max 65</span>
            </div>
          </div>

          {/* Limitations */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Limitations & Transparency</h3>
            <ul className="space-y-1 text-sm text-slate-700 list-disc list-inside">
              <li>Does not replace peer review or GRADE/Cochrane frameworks</li>
              <li>Scoring is deterministic and reproducible based on transparent criteria</li>
              <li>AI cannot detect fraud or fabricated data (relies on reported information)</li>
              <li>Intended to support rapid evidence discovery and structured research visibility</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
